import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { 
    collection, query, where, orderBy, onSnapshot, 
    addDoc, updateDoc, doc, serverTimestamp, getDocs, increment 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
const GlobalChatPage = () => {
    const { currentUser } = useAuth();
    
    // UI State
    const [threads, setThreads] = useState([]);
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoadingThreads, setIsLoadingThreads] = useState(true);
    
    // Attachment State
    const [attachedImage, setAttachedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSending, setIsSending] = useState(false);
    
    // Modal & User Selection State
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null); // Reference to the file input

    // Auto-scroll to bottom of chat
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // AUTO-CLEAR UNREAD IF CURRENTLY IN THREAD
    useEffect(() => {
        if (!activeThreadId || !currentUser) return;
        
        const activeThread = threads.find(t => t.id === activeThreadId);
        
        // If I am actively looking at this thread, and my unread count goes above 0, instantly clear it.
        if (activeThread && activeThread.unreadCounts?.[currentUser.uid] > 0) {
            updateDoc(doc(db, 'superAdmin_chats', activeThreadId), {
                [`unreadCounts.${currentUser.uid}`]: 0
            }).catch(err => console.error("Failed to auto-clear unread:", err));
        }
    }, [messages, activeThreadId, currentUser, threads]);

    // 1. LISTEN TO THREADS (The Inbox)
    useEffect(() => {
        if (!currentUser?.uid) return;

        const q = query(
            collection(db, 'superAdmin_chats'),
            where('participants', 'array-contains', currentUser.uid),
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedThreads = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setThreads(fetchedThreads);
            setIsLoadingThreads(false);
        }, (error) => {
            console.error("Error fetching threads:", error);
            setIsLoadingThreads(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // 2. LISTEN TO ACTIVE CHAT MESSAGES
    useEffect(() => {
        if (!activeThreadId) {
            setMessages([]);
            return;
        }

        const q = query(
            collection(db, `superAdmin_chats/${activeThreadId}/chats`),
            orderBy('sentOn', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(fetchedMessages);
        });

        return () => unsubscribe();
    }, [activeThreadId]);

    // 3. FETCH USERS FOR NEW CHAT
    const handleOpenNewChatModal = async () => {
        setIsNewChatModalOpen(true);
        if (availableUsers.length > 0) return;

        try {
            const usersSnap = await getDocs(collection(db, 'users'));
            const usersList = usersSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(u => u.id !== currentUser.uid);
            setAvailableUsers(usersList);
        } catch (error) {
            console.error("Failed to fetch users", error);
        }
    };

    // 4. CREATE NEW THREAD
    const handleCreateThread = async () => {
        if (selectedUserIds.length === 0) return alert("Select at least one user.");

        const participants = [currentUser.uid, ...selectedUserIds];
        const type = selectedUserIds.length === 1 ? 'direct' : 'group';
        
        const participantNames = { [currentUser.uid]: currentUser.email.split('@')[0] };
        selectedUserIds.forEach(id => {
            const user = availableUsers.find(u => u.id === id);
            participantNames[id] = user ? (user.username || user.email) : 'Unknown';
        });

        const unreadCounts = {};
        participants.forEach(id => unreadCounts[id] = 0);

        try {
            const newThreadData = {
                type,
                participants,
                participantNames,
                unreadCounts,
                lastMessage: { message: "Chat started", sentBy: currentUser.uid },
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, 'superAdmin_chats'), newThreadData);
            
            setIsNewChatModalOpen(false);
            setSelectedUserIds([]);
            setActiveThreadId(docRef.id);

        } catch (error) {
            console.error("Error creating thread:", error);
            alert("Failed to create chat.");
        }
    };

    // --- Handle Image Selection ---
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert("File is too large. Please select an image under 5MB.");
                return;
            }
            setAttachedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const clearAttachment = () => {
        setAttachedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // 5. SEND MESSAGE
    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        const messageText = newMessage.trim();
        if (!messageText && !attachedImage) return; // Prevent empty sends
        if (!activeThreadId) return;

        setIsSending(true);
        setNewMessage('');

        try {
            let downloadUrl = null;
            let messageType = 'text';

            // Phase 1: Upload Image (if exists)
            if (attachedImage) {
                const safeName = attachedImage.name.replace(/\s+/g, '_');
                const imageRef = ref(storage, `superAdmin_chats/${activeThreadId}/${Date.now()}_${safeName}`);
                await uploadBytes(imageRef, attachedImage);
                downloadUrl = await getDownloadURL(imageRef);
                messageType = 'image';
            }

            // Phase 2: Write Message to Firestore
            const messagePayload = {
                message: messageText,
                sentByUid: currentUser.uid,
                sentOn: serverTimestamp(),
                type: messageType,
                readBy: [currentUser.uid]
            };
            if (downloadUrl) messagePayload.imageUrl = downloadUrl; // Attach URL if image

            await addDoc(collection(db, `superAdmin_chats/${activeThreadId}/chats`), messagePayload);

            // Phase 3: Update Thread Metadata
            const activeThread = threads.find(t => t.id === activeThreadId);
            const displayPreview = attachedImage ? (messageText ? `📷 ${messageText}` : '📷 Image') : messageText;

            const updates = {
                lastMessage: {
                    message: displayPreview,
                    sentBy: currentUser.uid,
                    timestamp: new Date()
                },
                updatedAt: serverTimestamp()
            };

            activeThread.participants.forEach(participantId => {
                if (participantId !== currentUser.uid) {
                    updates[`unreadCounts.${participantId}`] = increment(1);
                }
            });

            await updateDoc(doc(db, 'superAdmin_chats', activeThreadId), updates);

            // Cleanup
            clearAttachment();

        } catch (error) {
            console.error("Error sending message:", error);
            alert("Failed to send message. Check your connection.");
        } finally {
            setIsSending(false);
        }
    };

    // 6. SELECT THREAD & MARK AS READ
    const handleSelectThread = async (thread) => {
        setActiveThreadId(thread.id);
        
        if (thread.unreadCounts && thread.unreadCounts[currentUser.uid] > 0) {
            try {
                await updateDoc(doc(db, 'superAdmin_chats', thread.id), {
                    [`unreadCounts.${currentUser.uid}`]: 0
                });
            } catch (error) {
                console.error("Failed to clear unread count:", error);
            }
        }
    };

    const getThreadName = (thread) => {
        if (thread.type === 'group') return "Global Group Chat";
        const otherId = thread.participants.find(id => id !== currentUser.uid);
        return thread.participantNames?.[otherId] || 'Unknown User';
    };

    return (
        <div className="page-container" style={{ maxWidth: '1400px', margin: '0 auto', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            
            {/* MODAL: New Chat */}
            {isNewChatModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', width: '400px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                        <h2 style={{ marginTop: 0 }}>Start New Chat</h2>
                        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px' }}>
                            {availableUsers.map(u => (
                                <label key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '10px', cursor: 'pointer', borderBottom: '1px solid #edf2f7' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedUserIds.includes(u.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.id]);
                                            else setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                                        }}
                                        style={{ marginRight: '10px' }}
                                    />
                                    {u.username || u.email}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setIsNewChatModalOpen(false)} style={{ padding: '8px 16px', background: '#e2e8f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleCreateThread} style={{ padding: '8px 16px', background: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Start Chat</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="page-header" style={{ marginBottom: '20px' }}>
                <h1>Global Communications</h1>
            </div>

            <div style={{ display: 'flex', flex: 1, backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                
                {/* LEFT PANE: Inbox Sidebar */}
                <div style={{ width: '350px', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', backgroundColor: '#f7fafc' }}>
                    <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
                        <button 
                            onClick={handleOpenNewChatModal}
                            style={{ width: '100%', padding: '12px', backgroundColor: '#2b6cb0', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            + New Message
                        </button>
                    </div>
                    
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {isLoadingThreads ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>Loading chats...</div>
                        ) : threads.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0', fontSize: '14px' }}>No active conversations.</div>
                        ) : (
                            threads.map(thread => {
                                const myUnreadCount = thread.unreadCounts?.[currentUser.uid] || 0;
                                
                                return (
                                    <div 
                                        key={thread.id} 
                                        onClick={() => handleSelectThread(thread)}
                                        style={{ 
                                            padding: '15px 20px', 
                                            borderBottom: '1px solid #edf2f7', 
                                            cursor: 'pointer',
                                            backgroundColor: activeThreadId === thread.id ? '#ebf8ff' : 'transparent',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <div style={{ fontWeight: myUnreadCount > 0 ? '800' : '600', color: '#2d3748' }}>
                                                {getThreadName(thread)}
                                            </div>
                                            {myUnreadCount > 0 && (
                                                <div style={{ backgroundColor: '#e53e3e', color: 'white', fontSize: '11px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '10px' }}>
                                                    {myUnreadCount}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '13px', color: myUnreadCount > 0 ? '#4a5568' : '#718096', fontWeight: myUnreadCount > 0 ? '600' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {thread.lastMessage?.message || 'Started a chat'}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* RIGHT PANE: Chat Stage */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff' }}>
                    {activeThreadId ? (
                        <>
                            {/* Chat Header */}
                            <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                <h3 style={{ margin: 0, color: '#2d3748' }}>
                                    {getThreadName(threads.find(t => t.id === activeThreadId) || {})}
                                </h3>
                            </div>

                            {/* Messages Area */}
                            <div style={{ flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#fdfdfd' }}>
                                {(() => {
                                    const activeThread = threads.find(t => t.id === activeThreadId) || {};
                                    const namesMap = activeThread.participantNames || {};

                                    return messages.map(msg => {
                                        const isMine = msg.sentByUid === currentUser.uid;
                                        const senderName = namesMap[msg.sentByUid] || 'Unknown User';

                                        return (
                                            <div key={msg.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: '15px' }}>
                                                <div style={{ 
                                                    maxWidth: '70%', 
                                                    padding: '12px 16px', 
                                                    borderRadius: '12px', 
                                                    backgroundColor: isMine ? '#3182ce' : '#edf2f7',
                                                    color: isMine ? 'white' : '#2d3748',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}>
                                                    {!isMine && <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', opacity: 0.6 }}>{senderName}</div>}
                                                    
                                                    {/* IMAGE RENDERER */}
                                                    {msg.type === 'image' && msg.imageUrl && (
                                                        <img 
                                                            src={msg.imageUrl} 
                                                            alt="Attachment" 
                                                            onClick={() => window.open(msg.imageUrl, '_blank')}
                                                            title="Click to view full size"
                                                            style={{ 
                                                                maxHeight: '200px', // Restricts giant vertical images
                                                                maxWidth: '100%', 
                                                                borderRadius: '8px', 
                                                                marginBottom: msg.message ? '8px' : '0', 
                                                                display: 'block',
                                                                cursor: 'pointer', // Changes mouse to a pointer hand
                                                                objectFit: 'cover', // Prevents stretching
                                                                border: isMine ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e2e8f0',
                                                                transition: 'opacity 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
                                                            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                                                        />
                                                    )}
                                                    
                                                    {msg.message && <div style={{ fontSize: '14px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{msg.message}</div>}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Area */}
                            <div style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: 'white' }}>
                                {/* Image Preview Box */}
                                {imagePreview && (
                                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '10px' }}>
                                        <img src={imagePreview} alt="Preview" style={{ height: '60px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                                        <button 
                                            onClick={clearAttachment} 
                                            style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            ✖
                                        </button>
                                    </div>
                                )}

                                <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    
                                    {/* HIDDEN FILE INPUT & PAPERCLIP BUTTON */}
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        ref={fileInputRef} 
                                        onChange={handleImageSelect} 
                                        style={{ display: 'none' }} 
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => fileInputRef.current.click()}
                                        style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '5px', color: '#718096' }}
                                        title="Attach Image"
                                    >
                                        📎
                                    </button>

                                    <input 
                                        type="text" 
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type a message..." 
                                        disabled={isSending}
                                        style={{ flex: 1, padding: '12px 15px', borderRadius: '24px', border: '1px solid #cbd5e0', outline: 'none', fontSize: '15px' }}
                                    />
                                    <button 
                                        type="submit" 
                                        disabled={isSending || (!newMessage.trim() && !attachedImage)}
                                        style={{ padding: '0 25px', height: '44px', backgroundColor: (newMessage.trim() || attachedImage) ? '#38a169' : '#a0aec0', color: 'white', border: 'none', borderRadius: '24px', fontWeight: 'bold', cursor: (newMessage.trim() || attachedImage) ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}
                                    >
                                        {isSending ? 'Sending...' : 'Send'}
                                    </button>
                                </form>
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#a0aec0' }}>
                            <div style={{ fontSize: '48px', marginBottom: '15px' }}>💬</div>
                            <h2 style={{ margin: 0, fontWeight: '500' }}>Your Messages</h2>
                            <p style={{ marginTop: '5px' }}>Select a conversation or start a new one.</p>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default GlobalChatPage;